import { PopupLayer } from '@features/shared/components/PopupLayer'
import { ModalShell } from '@features/shared/components/ModalShell'
import { useDialogLayers } from '@features/shared/components/popupLayers'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  getPermissionActions,
  getPermissionImplicationRules,
  getResourceTypes,
  getAllUserGroups,
} from '@features/permissions/api/permissions.api'
import {
  getPermissionToggleChanges,
  getEffectiveScopedPermissions,
  scopedPermissionKey,
  type ScopedPermission,
} from '@features/permissions/logic/actionImplications'
import { permissionsQueryKeys } from '@features/permissions/api/queryKeys'
import {
  getPermissionActionLabel,
  TABLE_ACTION_NAMES,
} from '@features/permissions/logic/actionLabels'
import {
  ADMINISTRATORS_GET_FULL_ACCESS,
  getDefaultGrantGroupIds,
  getNewGrantGaps,
  getOnlyGroupId,
  getOwnGroupIds,
} from '@features/permissions/logic/newResourceGrants'
import { useSettingsStore } from '@features/settings/stores/useSettingsStore'
import {
  refreshPermissionAssignments,
  refreshPermissionTables,
} from '@features/permissions/logic/permissionCaches'
import { FieldInput } from '@features/shared/components/FieldInput'
import { TabMenu } from '@features/shared/components/TabMenu'
import { Tooltip } from '@features/shared/components/Tooltip'
import { UnsavedChangesQuestion } from '@features/shared/components/UnsavedChangesModal'
import { useDismissGuard } from '@features/shared/hooks/useDismissGuard'
import { apiClient, getErrorMessage } from '@features/shared/api/client'
import { useQueryClient } from '@tanstack/react-query'
import { tablesQueryKeys } from '../api/queryKeys'
import { invalidateTableSearches } from '../logic/tableMetadataCaches'
import type { CreateTableRequest, TableDetail } from '../api/contracts'
import { useToast } from '@features/shared/components/Toast/ToastContext'
import { sidebarQueryKeys } from '@features/sidebar/api/queryKeys'

interface CreateTableDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (table: TableDetail, close: boolean) => void
}

const toSlug = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')

export default function CreateTableDialog({ open, onClose, onCreated }: CreateTableDialogProps) {
  const dialogLayers = useDialogLayers()
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const [activeTab, setActiveTab] = useState<'overview' | 'permissions'>('overview')
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const groupsQuery = useQuery({
    queryKey: permissionsQueryKeys.permissionsUserGroups,
    queryFn: getAllUserGroups,
    enabled: open,
  })
  const actionsQuery = useQuery({
    queryKey: permissionsQueryKeys.permissionsActions,
    queryFn: getPermissionActions,
    enabled: open,
    staleTime: Infinity,
  })
  const resourceTypesQuery = useQuery({
    queryKey: permissionsQueryKeys.permissionsResourceTypes,
    queryFn: getResourceTypes,
    enabled: open,
    staleTime: Infinity,
  })
  const implicationsQuery = useQuery({
    queryKey: permissionsQueryKeys.permissionsImplicationRules,
    queryFn: getPermissionImplicationRules,
    enabled: open,
    staleTime: Infinity,
  })
  const actions = useMemo(
    () =>
      TABLE_ACTION_NAMES.flatMap((name) => {
        const action = actionsQuery.data?.find((item) => item.name === name)
        return action
          ? [
              {
                ...action,
                label: getPermissionActionLabel('table', name),
              },
            ]
          : []
      }),
    [actionsQuery.data]
  )
  const groups = useMemo(
    () =>
      [...(groupsQuery.data ?? [])].sort((a, b) =>
        a.user_group_name.localeCompare(b.user_group_name)
      ),
    [groupsQuery.data]
  )
  const tableTypeId = resourceTypesQuery.data?.find((type) => type.name === 'table')?.id ?? null
  const columnTypeId = resourceTypesQuery.data?.find((type) => type.name === 'column')?.id ?? null
  const allowedActionIds = useMemo(() => new Set(actions.map((action) => action.id)), [actions])
  const implicationRules = useMemo(() => implicationsQuery.data ?? [], [implicationsQuery.data])
  // A creation draft has no persisted target ID yet; the shared placeholder preserves
  // resource identity across implications without being included in the API payload.
  const draftTableId = '__new_table__'
  const scoped = useCallback(
    (actionId: string): ScopedPermission => ({
      actionId,
      targetResourceId: draftTableId,
      targetResourceTypeId: tableTypeId,
      resultResourceTypeId:
        actions.find((action) => action.id === actionId)?.name === 'create' ? columnTypeId : null,
    }),
    [actions, tableTypeId, columnTypeId]
  )
  const isExplicit = (groupId: string, actionId: string) =>
    Boolean(selected[`${groupId}:${actionId}`])
  const groupScopes = useCallback(
    (groupId: string, state: Record<string, boolean>) =>
      new Map(
        actions
          .filter((action) => state[`${groupId}:${action.id}`])
          .map((action) => {
            const permission = scoped(action.id)
            return [scopedPermissionKey(permission), permission] as const
          })
      ),
    [actions, scoped]
  )
  const effectiveByGroup = useMemo(
    () =>
      new Map(
        groups.map((group) => [
          group.user_group_id,
          getEffectiveScopedPermissions(
            implicationRules,
            groupScopes(group.user_group_id, selected).values()
          ),
        ])
      ),
    [groups, selected, implicationRules, groupScopes]
  )
  const isChecked = (groupId: string, actionId: string) =>
    effectiveByGroup.get(groupId)?.has(scopedPermissionKey(scoped(actionId))) ?? false
  const togglePermission = (groupId: string, actionId: string) => {
    if (!tableTypeId || !columnTypeId) return
    setSelected((current) => {
      const explicit = groupScopes(groupId, current)
      const changes = getPermissionToggleChanges(
        implicationRules,
        scoped(actionId),
        explicit,
        allowedActionIds
      )
      const next = { ...current }
      for (const key of changes.remove) {
        const permission = explicit.get(key)
        if (permission) delete next[`${groupId}:${permission.actionId}`]
      }
      for (const permission of changes.add.values())
        next[`${groupId}:${permission.actionId}`] = true
      return next
    })
  }
  const permissions = groups.flatMap((group) =>
    actions
      .filter((action) => isExplicit(group.user_group_id, action.id))
      .map((action) => ({
        request_type: 'create' as const,
        user_group_id: group.user_group_id,
        action_id: action.id,
        result_resource_type_id: action.name === 'create' ? columnTypeId : null,
        value: 1,
      }))
  )
  // Who must get what (features/permissions/logic/newResourceGrants.ts): an
  // administrator gives each permission to some group; anyone else gives
  // themselves every permission through their own groups.
  const appUserId = useSettingsStore((state) => state.appUserId)
  const isAdministrator = useSettingsStore((state) => state.isAdministrator)
  const ownGroupIds = useMemo(() => getOwnGroupIds(groups, appUserId), [groups, appUserId])
  const onlyGroupId = getOnlyGroupId(groups)
  const grantGaps = getNewGrantGaps(actions, {
    groupIds: groups.map((group) => group.user_group_id),
    ownGroupIds,
    isAdministrator,
    isChecked,
  })
  // Each opening starts from the defaults once the lists are in: the only
  // group, or an administrator's own administrator groups, with everything.
  const defaultsAppliedRef = useRef(false)
  useEffect(() => {
    if (!open) {
      defaultsAppliedRef.current = false
      return
    }
    if (defaultsAppliedRef.current || !groupsQuery.data || actions.length !== 5) return
    defaultsAppliedRef.current = true
    const defaults = getDefaultGrantGroupIds(groupsQuery.data, {
      userId: appUserId,
      isAdministrator,
    })
    if (defaults.length === 0) return
    setSelected(
      Object.fromEntries(
        defaults.flatMap((groupId) => actions.map((action) => [`${groupId}:${action.id}`, true]))
      )
    )
  }, [open, groupsQuery.data, actions, appUserId, isAdministrator])
  const permissionsLoading =
    groupsQuery.isPending ||
    actionsQuery.isPending ||
    resourceTypesQuery.isPending ||
    implicationsQuery.isPending
  const permissionsError =
    groupsQuery.isError ||
    actionsQuery.isError ||
    resourceTypesQuery.isError ||
    implicationsQuery.isError
  const permissionsInvalid =
    permissionsLoading ||
    permissionsError ||
    !tableTypeId ||
    !columnTypeId ||
    actions.length !== 5 ||
    groups.length === 0 ||
    grantGaps.missing.length > 0
  const [singular, setSingular] = useState('')
  const [plural, setPlural] = useState('')
  const [name, setName] = useState('')
  const [nameEdited, setNameEdited] = useState(false)
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [savingAction, setSavingAction] = useState<'create' | 'close' | null>(null)
  const [created, setCreated] = useState(false)
  const namesQuery = useQuery({
    queryKey: ['tables', 'names'],
    queryFn: async () =>
      (
        await apiClient.get<
          Array<{
            table_name: string
            table_name_display_singular: string | null
            table_name_display_plural: string | null
          }>
        >('/tables/names')
      ).data,
    enabled: open,
    staleTime: 0,
    retry: false,
  })
  const names = namesQuery.data ?? []
  const normalize = (value: string) => value.trim().toLocaleLowerCase()
  const systemNameError = names.some((table) => table.table_name === name)
    ? 'A table with this System Name already exists.'
    : null
  const singularError =
    singular.trim() &&
    names.some(
      (table) => normalize(table.table_name_display_singular ?? '') === normalize(singular)
    )
      ? 'A table with this Singular Display Name already exists.'
      : null
  const pluralError =
    plural.trim() &&
    names.some((table) => normalize(table.table_name_display_plural ?? '') === normalize(plural))
      ? 'A table with this Plural Display Name already exists.'
      : null
  const namesUnavailable = namesQuery.isPending || namesQuery.isFetching || namesQuery.isError
  useEffect(() => {
    if (open) return
    setActiveTab('overview')
    setSelected({})
    setSingular('')
    setPlural('')
    setName('')
    setNameEdited(false)
    setDescription('')
    setCreated(false)
    setSavingAction(null)
  }, [open])
  // A click outside or Escape asks before dropping what's been entered;
  // Cancel drops it.
  const dismissGuard = useDismissGuard({
    open,
    hasUnsavedChanges:
      !saving &&
      !created &&
      (Boolean(singular.trim() || plural.trim() || description.trim()) ||
        (nameEdited && Boolean(name)) ||
        Object.keys(selected).length > 0),
    onClose,
  })
  // Docked beside the page, the page stays usable; closing undocks it.
  const [isDocked, setIsDocked] = useState(false)
  useEffect(() => {
    if (!open) setIsDocked(false)
  }, [open])

  if (!open) return null

  const overviewInvalid =
    !singular.trim() ||
    !plural.trim() ||
    !/^[a-z][a-z0-9_]*$/.test(name) ||
    namesUnavailable ||
    Boolean(systemNameError || singularError || pluralError)
  const invalid = overviewInvalid || permissionsInvalid
  const submit = async (close: boolean) => {
    if (saving || invalid || created) return
    setSaving(true)
    setSavingAction(close ? 'close' : 'create')
    try {
      const payload: CreateTableRequest = {
        table_name: name,
        table_name_display_singular: singular.trim(),
        table_name_display_plural: plural.trim(),
        table_description_user: description.trim() || null,
        permissions,
      }
      const { data } = await apiClient.post<TableDetail>('/tables', payload)
      setCreated(true)
      // Each group's tabs list tables and read grants from their own caches;
      // a new table always comes with grants, so both now miss it.
      void refreshPermissionTables(queryClient)
      void refreshPermissionAssignments(queryClient)
      // Searches (Cmd-K and its preview, the sidebar's) keep their own copies.
      void invalidateTableSearches(queryClient)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: tablesQueryKeys.tables }),
        queryClient.invalidateQueries({ queryKey: sidebarQueryKeys.tableOrganization }),
      ])
      addToast({ type: 'success', title: 'Table created', message: `"${plural}" was created.` })
      onCreated(data, close)
    } catch (error) {
      addToast({
        type: 'error',
        title: "Couldn't create table",
        message: getErrorMessage(error),
      })
    } finally {
      setSaving(false)
      setSavingAction(null)
    }
  }

  const footer = (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        disabled={saving}
        onClick={onClose}
        className="rounded-md px-3 py-1.5 text-sm font-medium text-data transition-colors hover:bg-lighter hover:text-default disabled:cursor-not-allowed disabled:text-faint disabled:hover:bg-transparent"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={saving || invalid || created}
        className="rounded-md border border-blue-500 px-3 py-1.5 text-sm font-medium text-[color:var(--text-info)] transition-colors hover:border-blue-600 hover:bg-[color:var(--surface-info-subtle)] hover:text-[color:var(--text-info-strong)] disabled:cursor-not-allowed disabled:border-[color:var(--border-default)] disabled:text-[color:var(--text-faint)] disabled:hover:bg-transparent"
      >
        {savingAction === 'create' ? 'Creating...' : 'Create'}
      </button>
      <button
        type="button"
        disabled={saving || invalid || created}
        onClick={() => void submit(true)}
        className="rounded-md bg-[color:var(--action-brand-bg)] px-3 py-1.5 text-sm font-medium text-[color:var(--action-brand-text)] transition-colors enabled:hover:bg-[color:var(--action-brand-bg-hover)] disabled:cursor-not-allowed disabled:bg-[color:var(--bg-muted)] disabled:text-[color:var(--text-muted)] disabled:opacity-40"
      >
        {savingAction === 'close' ? 'Creating...' : 'Create & Close'}
      </button>
    </div>
  )

  return (
    <>
      <ModalShell
        open
        title="Add Table"
        eyebrow="Table Settings"
        tabs={
          <TabMenu
            tabs={[
              {
                id: 'overview',
                label: 'Overview',
                indicator: overviewInvalid,
                indicatorTooltip: 'Tab has invalid or incomplete fields',
              },
              {
                id: 'permissions',
                label: 'Permissions',
                indicator: permissionsInvalid,
                indicatorTooltip: grantGaps.message,
              },
            ]}
            activeTab={activeTab}
            onChange={(id) => setActiveTab(id as 'overview' | 'permissions')}
          />
        }
        onClose={dismissGuard.requestClose}
        dismissible={!saving && !dismissGuard.isAsking}
        // A card beside the tables list, not over the page.
        modal={false}
        dock={{
          docked: isDocked,
          onDock: () => setIsDocked(true),
          onUndock: () => setIsDocked(false),
        }}
        onSubmit={(event) => {
          event.preventDefault()
          void submit(false)
        }}
        width="auto"
        className="right-4 top-28"
        style={{ width: 'min(820px, calc(100vw - 32px))', height: 560 }}
        contentClassName="column-manage-dialog-body popover-selector-scrollbar flex-1 px-3 pb-3 pt-4"
        footer={footer}
      >
        <fieldset disabled={saving} className="min-w-0 space-y-4 border-0 p-0">
          {activeTab === 'overview' ? (
            <>
              <FieldInput
                type="short-text"
                label="Singular Display Name"
                required
                value={singular}
                error={Boolean(singularError)}
                errorMessage={singularError ?? undefined}
                onChange={(value) => {
                  setSingular(value)
                  if (!nameEdited) setName(toSlug(value))
                }}
              />
              <FieldInput
                type="short-text"
                label="Plural Display Name"
                required
                value={plural}
                error={Boolean(pluralError)}
                errorMessage={pluralError ?? undefined}
                onChange={setPlural}
              />
              <FieldInput
                type="short-text"
                label="System Name"
                required
                value={name}
                error={Boolean(systemNameError)}
                errorMessage={systemNameError ?? undefined}
                onChange={(value) => {
                  setName(value)
                  setNameEdited(true)
                }}
              />
              <FieldInput
                type="long-text"
                label="Description"
                value={description}
                placeholder="Describe this table for users"
                resizeMode="custom"
                style={{ minHeight: 160 }}
                onChange={setDescription}
              />
            </>
          ) : (
            <div className="overflow-hidden rounded-lg border border-default">
              <div className="border-b border-default bg-lighter px-4 py-3">
                <h3 className="text-sm font-semibold text-default">Permission Groups</h3>
                <p className="mt-1 text-xs text-muted">
                  {onlyGroupId
                    ? 'The only group gets every permission on the new table.'
                    : 'Choose which groups get each permission on the new table. Implied permissions count.'}
                  {isAdministrator ? null : ` ${ADMINISTRATORS_GET_FULL_ACCESS}`}
                </p>
              </div>
              {permissionsLoading && <p className="p-4 text-sm text-muted">Loading permissions…</p>}
              {permissionsError && (
                <p className="p-4 text-sm text-danger">
                  Failed to load permission groups or actions.
                </p>
              )}
              {!permissionsLoading && !permissionsError && tableTypeId && (
                <div className="overflow-x-auto">
                  {grantGaps.missing.length > 0 && (
                    <p className="px-4 py-2 text-xs text-danger">
                      {grantGaps.message}:{' '}
                      {grantGaps.missing.map((action) => action.label).join(', ')}.
                    </p>
                  )}
                  <table className="w-full min-w-[560px] table-fixed border-collapse text-left text-sm text-data">
                    <thead className="bg-muted text-default">
                      <tr>
                        <th className="min-w-[220px] border-b border-strong px-4 py-3">
                          Permission Group
                        </th>
                        {actions.map((action) => (
                          <th
                            key={action.id}
                            className="w-[88px] border-b border-l border-strong px-2 py-3 text-center text-xs font-medium"
                          >
                            {action.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--border-default)]">
                      {groups.length === 0 && (
                        <tr>
                          <td colSpan={actions.length + 1} className="px-4 py-6 text-center">
                            No permission groups available.
                          </td>
                        </tr>
                      )}
                      {groups.map((group) => (
                        <tr key={group.user_group_id}>
                          <td className="px-4 py-3 font-medium text-default">
                            {group.user_group_name}
                            {group.filter && (
                              <span className="ml-2 text-xs text-faint">Dynamic</span>
                            )}
                          </td>
                          {actions.map((action) => {
                            return (
                              <td
                                key={action.id}
                                className="border-l border-default px-2 py-3 text-center"
                              >
                                <Tooltip
                                  content={
                                    group.user_group_id === onlyGroupId
                                      ? 'The only group gets every permission'
                                      : isChecked(group.user_group_id, action.id) &&
                                          !isExplicit(group.user_group_id, action.id)
                                        ? `${action.label} is implied by another permission`
                                        : null
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    aria-label={`${group.user_group_name}: ${action.label}`}
                                    disabled={group.user_group_id === onlyGroupId}
                                    checked={isChecked(group.user_group_id, action.id)}
                                    onChange={() =>
                                      togglePermission(group.user_group_id, action.id)
                                    }
                                    className="h-4 w-4 rounded border-strong text-[color:var(--accent-info)]"
                                  />
                                </Tooltip>
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </fieldset>
      </ModalShell>
      <PopupLayer layer={dialogLayers.menu}>
        <UnsavedChangesQuestion
          guard={dismissGuard}
          message="This table hasn't been created yet."
          saveLabel="Create & Close"
          onSave={invalid || created ? null : () => void submit(true)}
        />
      </PopupLayer>
    </>
  )
}
